import { useAuthStore } from '@/features/auth/store';

/**
 * User-scoping for persisted (localStorage) stores.
 *
 * WHY THIS EXISTS — CONFIDENTIALITY. localStorage is per-browser, NOT per-user.
 * Any store that persists client-derived data (matter names, party names,
 * briefs, timers) will, on a shared machine, surface one barrister's data to
 * the NEXT account that signs in — a real breach (seen on the "Jump back in"
 * dashboard strip). Server access control still blocks the underlying records,
 * but the cached NAMES/NUMBERS leak on screen. This closes the whole class.
 *
 * A persisted store opts in by:
 *   1. tracking `ownerUserId: string | null` in its state,
 *   2. exposing `reset(userId)` that wipes owner-scoped data + re-stamps owner,
 *   3. calling `registerUserScopedStore({ getOwner, reset })` at module load.
 *
 * We then reconcile once now (covers page reload) and on every auth change
 * (covers logout + account switch), wiping whenever the cache's owner no longer
 * matches the signed-in user.
 */

/** The currently signed-in user id, or null when logged out. */
export const currentOwnerId = (): string | null => useAuthStore.getState().user?.id ?? null;

export function registerUserScopedStore(store: {
  getOwner: () => string | null;
  reset: (userId: string | null) => void;
}): void {
  const reconcile = (uid: string | null) => {
    if (store.getOwner() !== uid) store.reset(uid);
  };
  reconcile(currentOwnerId());
  useAuthStore.subscribe((s) => reconcile(s.user?.id ?? null));
}
